import React, { useMemo, useEffect, useState } from 'react';
import useStageResults from '../../../useStageResults';
import { buildTestLeaderboardRows } from '../utils/testResults';
import '../../../desktopstyle.css';

const guestTextColors = [
  '#63AA57', '#6EB9E3', '#758AF8', '#8DEACE',
  '#B6F875', '#A2DDEA', '#9988FF', '#BECEF6', '#BCEAA0',
  '#ECA3A3', '#F5FF88', '#F875D3', '#FFBEA2', '#FFE5A2'
];

const createColorMapping = (data) => {
  const colorMapping = {};
  let colorIndex = 0;
  data.forEach((item) => {
    const inGroup = (item.inGroup || '').trim();
    if (!inGroup || colorMapping[inGroup]) return;
    colorMapping[inGroup] = guestTextColors[colorIndex % guestTextColors.length];
    colorIndex++;
  });
  return colorMapping;
};

const StageTestResultTable = ({
  selectedEventID,
  selectedCompetitionID,
  selectedGroupID,
  selectedStageID,
  selectedClassificationID,
}) => {
  const { stageResults, loading, error } = useStageResults(
    selectedEventID,
    selectedCompetitionID,
    selectedStageID,
    selectedClassificationID,
    selectedGroupID
  );

  const processedResults = useMemo(
    () =>
      buildTestLeaderboardRows(stageResults).map((r) => ({
        positionNumber: r.positionNumber,
        driver:         r.driverName,
        coDriver:       r.coDriverName,
        car:            r.car,
        inGroup:        r.inGroup,
        bestTime:       r.resultTime,
        bestSpeed:      r.bestSpeed,
        allRuns:        r.allRuns || [],
      })),
    [stageResults]
  );

  const maxRuns = useMemo(() => {
    if (!processedResults.length) return 0;
    return processedResults.reduce((max, r) => Math.max(max, r.allRuns.length), 0);
  }, [processedResults]);

  const [colorMapping, setColorMapping] = useState({});
  useEffect(() => {
    setColorMapping(createColorMapping(processedResults));
  }, [processedResults]);

  if (loading) {
    return <div className="loading-spinner" />;
  }

  if (error) {
    return (
      <div className="notification has-text-centered mt-1">
        <strong>{error}</strong>
      </div>
    );
  }

  if (!processedResults.length) {
    return (
      <div className="notification has-text-centered mt-1">
        <strong>Brak danych</strong>
      </div>
    );
  }

  return (
    <div className="dskt-table-container">
      <div className="title">Czasy na odcinku</div>
      <div className="tab-container">
        <table>
          <thead>
            <tr className="table-header">
              <th className="table-cell">Poz</th>
              <th className="table-cell">#</th>
              <th className="table-cell">Załoga</th>
              <th className="table-cell">Samochód &amp; Klasa</th>
              <th className="table-cell">Najlepszy czas</th>
              {Array.from({ length: maxRuns }).map((_, i) => (
                <th key={i} className="table-cell">Przejazd {i + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedResults.map((r, index) => (
              <tr key={`${r.driver}-${r.coDriver}`} className="table-row">
                <td className="table-cell">
                  <div className="table-cell-position">{index + 1}</div>
                </td>
                <td className="table-cell">
                  <div className="position-badge-text">
                    <div className="position-badge">{r.positionNumber}</div>
                  </div>
                </td>
                <td className="table-cell table-cell-name">
                  {r.driver}<br />{r.coDriver}
                </td>
                <td className="table-cell table-cell-car">
                  <div>{r.car}</div>
                  <div
                    className="guest-badge"
                    style={{ background: colorMapping[(r.inGroup || '').trim()] || 'transparent' }}
                  >
                    <div className="guest-badge-text">{r.inGroup}</div>
                  </div>
                </td>
                <td className="table-cell table-cell-time">
                  <div style={{ fontWeight: 700 }}>{r.bestTime}</div>
                </td>
                {Array.from({ length: maxRuns }).map((_, ri) => {
                  const run = r.allRuns[ri];
                  return (
                    <td key={ri} className="table-cell">
                      <div style={{ fontWeight: run?.isBest ? 700 : 400 }}>
                        {run ? run.time || '--' : '--'}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StageTestResultTable;
